import {
  Box,
  Button,
  ButtonGroup,
  ButtonProps,
  DatePicker,
  Divider,
  Icon,
  InlineStack,
  OptionList,
  Popover,
  Select,
  TextField,
  useBreakpoints,
} from "@shopify/polaris"
import { ArrowRightIcon, CalendarIcon } from "@shopify/polaris-icons"
import dayjs from "dayjs"
import { produce } from "immer"
import { isEqual } from "lodash-es"
import { useDraft } from "polaris-enhance"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { disabledRange, subtractDay, today } from "@/utils/dayjs"

export enum DayjsFormat {
  "YMD" = "YYYY-MM-DD",
  "YMDHM" = "YYYY-MM-DD HH:mm:ss Z",
  "MDYHMS" = "MMM DD, YYYY HH:mm:ss",
}

interface DateObj {
  start: Date
  end: Date
  year: number
  month: number
}

interface useQueryDateSelectProps {
  date: {
    day?: number
    subscribed_at_min?: string
    subscribed_at_max?: string
  }
  onSubmit(data: { data: DateObj; params: any }): void
}

const getInitData = (date: {
  day?: number
  subscribed_at_min?: string
  subscribed_at_max?: string
}) => {
  if (date.day) {
    return getDate(date.day)
  }
  const _newInputRange = calcNewInputRange(
    [formatDate(date.subscribed_at_min), formatDate(date.subscribed_at_max)],
    0,
  )
  return {
    start: dayjs(_newInputRange[0]).startOf("day").toDate(),
    end: dayjs(_newInputRange[1]).endOf("day").toDate(),
    month: dayjs(_newInputRange[0]).month(),
    year: dayjs(_newInputRange[0]).year(),
  }
}

export const useQueryDateSelect = (props: useQueryDateSelectProps) => {
  const { onSubmit, date } = props
  // 语言切换/语言包异步加载完成时，i18n.language 变化驱动 text 文案重新计算
  const {
    i18n: { language },
  } = useTranslation()
  const [selected, setSelected] = useState<SelectDay | "">(SelectDay.last60days)
  const initData = getInitData(date)
  const { data, isChanged, setData, copiedData, setCopiedData } =
    useDraft(initData)
  const { smUp } = useBreakpoints()
  const [active, setActive] = useState(false)
  const [inputRange, setInputRange] = useState(getRange(data))

  // optionList和select框 change事件
  function handleValueChange(val) {
    setSelected(val)
    if (val !== "") {
      const e = getDate(Number(val))
      setData(
        produce((data) => {
          data.start = e.start
          data.end = e.end
          data.month = e.month
          data.year = e.year
        }),
      )
      setInputRange(getRange(e))
    }
  }

  // 时间选择器 change事件
  function handleDateChange(e) {
    setData(
      produce((data) => {
        data.start = e.start
        data.end = e.end
      }),
    )
    const newInputRange = getRange(e)
    setInputRange(newInputRange)
    setSelected(findItem(newInputRange)?.value ?? "")
  }

  // 输入框事件
  function handleInputChange(val, index) {
    const newInputRange = [...inputRange]
    newInputRange[index] = val
    // 设置输入框的值
    setInputRange(newInputRange)

    // 判断是否是一个正常日期字符串
    if (dayjs(val).isValid() && /\d{4}-\d{2}-\d{2}/.test(val)) {
      const _newInputRange = calcNewInputRange(newInputRange, index)
      //   日期不能小于开始日期
      setInputRange([..._newInputRange])
      setData(rangeToDateObj(_newInputRange))
      setSelected(findItem(_newInputRange)?.value ?? "")
    }
  }

  function handleBlur(index) {
    if (!dayjs(inputRange[index]).isValid()) {
      setInputRange(
        produce((data) => {
          data[index] = t("InvalidDate")
        }),
      )
    } else {
      setInputRange(
        produce((data) => {
          data[index] = formatDate(dayjs(data[index]))
        }),
      )
    }
  }

  const text = useMemo(() => {
    const rangeInput = getRange(copiedData)
    const item = findItem(rangeInput)
    if (item) {
      return item.label
    }
    return rangeInput.join(" - ")
    // language 作为依赖：语言变化时重新计算已选项的翻译 label
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copiedData, language])

  function resetByDate(date: DateObj) {
    setData({ ...date })
    const newInputRange = getRange(date)
    setInputRange(newInputRange)
    setSelected(findItem(newInputRange)?.value ?? "")
  }

  const node = (
    <Popover
      active={active}
      activator={
        <Button
          icon={CalendarIcon}
          onClick={() => {
            const val = !active
            if (val) {
              resetByDate(copiedData)
            }
            setActive(val)
          }}
        >
          {text}
        </Button>
      }
      onClose={() => {
        setActive(false)
      }}
      fluidContent
      preferredAlignment="right"
    >
      <InlineStack wrap={false}>
        {smUp && (
          <Box width="142px">
            <OptionList
              options={[
                ...optionValue2string(options()),
                { label: t("Custom"), value: "" },
              ]}
              onChange={(val) => {
                handleValueChange(val[0])
              }}
              selected={[selected]}
            />
          </Box>
        )}

        <Box padding={"400"} width="548px">
          {!smUp && (
            <div className="mb-4">
              <Select
                label={t("DateRange")}
                options={[
                  ...optionValue2string(options()),
                  { label: t("Custom"), value: "" },
                ]}
                onChange={handleValueChange}
                value={selected}
              />
            </div>
          )}
          <div className="mb-4 flex items-center gap-2">
            <div className="flex-1">
              <TextField
                label=""
                placeholder={DayjsFormat.YMD}
                value={inputRange[0]}
                onChange={(val) => handleInputChange(val, 0)}
                onBlur={() => handleBlur(0)}
              />
            </div>
            <div className="text-[#8a8a8a]">
              <Icon source={ArrowRightIcon} />
            </div>
            <div className="flex-1">
              <TextField
                label=""
                placeholder={DayjsFormat.YMD}
                value={inputRange[1]}
                onChange={(val) => handleInputChange(val, 1)}
                onBlur={() => handleBlur(1)}
              />
            </div>
          </div>
          <DatePicker
            month={data.month}
            year={data.year}
            onChange={handleDateChange}
            disableDatesBefore={disabledRange()[0]}
            disableDatesAfter={disabledRange()[1]}
            onMonthChange={(month, year) => {
              setData(
                produce((data) => {
                  data.month = month
                  data.year = year
                }),
              )
            }}
            selected={{
              start: data.start,
              end: data.end,
            }}
            multiMonth
            allowRange
          />
        </Box>
      </InlineStack>
      <Divider />
      <Box padding={"400"}>
        <InlineStack align="end">
          <ButtonGroup>
            <Button
              onClick={() => {
                setActive(false)
              }}
            >
              {t("Cancel")}{" "}
            </Button>
            <Button
              variant="primary"
              disabled={!isChanged}
              onClick={async () => {
                setCopiedData({ ...data })
                setActive(false)
                onSubmit({
                  data,
                  params: selected
                    ? { day: +selected }
                    : {
                        subscribed_at_min: formatDate(data.start),
                        subscribed_at_max: formatDate(data.end),
                      },
                })
              }}
            >
              {t("Apply")}{" "}
            </Button>
          </ButtonGroup>
        </InlineStack>
      </Box>
    </Popover>
  )
  return {
    node,
    data,
    resetByDate,
  }
}

function optionValue2string(options: any[]) {
  return options.map((item) => {
    return {
      ...item,
      value: String(item.value),
    }
  })
}

export enum SelectDay {
  today = "1",
  last7days = "7",
  last30days = "30",
  last60days = "60",
  last90days = "90",
  last365days = "365",
}

const options = () => [
  { value: SelectDay.today, label: t("Today") },
  {
    value: SelectDay.last7days,
    label: t("Last_7Days"),
  },
  {
    value: SelectDay.last30days,
    label: t("Last_30Days"),
  },
  {
    value: SelectDay.last60days,
    label: t("Last_60Days"),
  },
  {
    value: SelectDay.last90days,
    label: t("Last_90Days"),
  },
  {
    value: SelectDay.last365days,
    label: t("Last_365Days"),
  },
]

function getDate(val: number) {
  const formatStart = subtractDay(val - 1)
  const formatEnd = today()
  const start = dayjs(formatStart)
  const end = dayjs(formatEnd)
  return {
    start: start.toDate(),
    end: end.toDate(),
    year: start.year(),
    month: start.month(),
  }
}

function formatDate(val) {
  return dayjs(val).format(DayjsFormat.YMD)
}

function findItem(newInputRange: string[]) {
  const res = options().find((item) => {
    const itemFormat = [
      formatDate(getDate(Number(item.value)).start),
      formatDate(getDate(Number(item.value)).end),
    ]
    return isEqual(itemFormat, newInputRange)
  })
  return res
}

function getRange(e: DateObj) {
  return [formatDate(e.start), formatDate(e.end)]
}

function rangeToDateObj(newInputRange: string[]) {
  return {
    start: dayjs(newInputRange[0]).startOf("day").toDate(),
    end: dayjs(newInputRange[1]).endOf("day").toDate(),
    month: dayjs(newInputRange[0]).month(),
    year: dayjs(newInputRange[0]).year(),
  }
}

// 输入框输入日期范围计算规则
function calcNewInputRange(newInputRange: string[], index: number) {
  // 输入的是开始日期
  if (index === 0) {
    // 如果开始日期大于结束日期
    if (dayjs(newInputRange[0]).isAfter(dayjs(newInputRange[1]))) {
      // 设置结束日期为开始日期
      newInputRange[1] = newInputRange[0]
    }
    // 如果开始日期小于disabled.0的日期
    if (dayjs(newInputRange[0]).isBefore(disabledRange()[0])) {
      newInputRange[0] = formatDate(disabledRange()[0])
    }
    // 如果开始日期大于disabled.1的日期
    if (dayjs(newInputRange[0]).isAfter(disabledRange()[1])) {
      newInputRange[0] = formatDate(disabledRange()[1])
      newInputRange[1] = formatDate(disabledRange()[1])
    }
  }
  //   输入的是结束日期
  if (index === 1) {
    // 如果结束日期小于开始日期
    if (dayjs(newInputRange[1]).isBefore(dayjs(newInputRange[0]))) {
      // 设置开始日期为结束日期
      newInputRange[0] = newInputRange[1]
    }
    // 如果结束日期小于disabled.0的日期
    if (dayjs(newInputRange[1]).isBefore(disabledRange()[0])) {
      newInputRange[0] = formatDate(disabledRange()[0])
      newInputRange[1] = formatDate(disabledRange()[0])
    }
    // 如果结束日期大于disabled.1的日期
    if (dayjs(newInputRange[1]).isAfter(disabledRange()[1])) {
      newInputRange[1] = formatDate(disabledRange()[1])
    }
  }

  return newInputRange
}
